import React, { useContext, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  AlertCircle,
  CheckCircle,
  Info,
  Calendar,
  User,
  Filter,
  ChevronLeft,
  ChevronRight,
  X,
  ExternalLink,
  Eye,
  Clock,
  Users,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { AuthContext } from "../../contexts/AuthContext";
import {
  getStudentNotices,
  markNoticeAsRead,
  Notice,
  hideNotice,
} from "../../services/notices";
import { getMyStudentProfile } from "../../services/students";

const StudentNotices: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);
  const queryClient = useQueryClient();

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, searchTerm]);

  const {
    data: noticesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["student-notices", currentPage],
    queryFn: () => getStudentNotices(currentPage, 10),
    enabled: !!user?.id,
  });

  const { data: studentData, isLoading: studentLoading } = useQuery({
    queryKey: ["student-profile"],
    queryFn: () => getMyStudentProfile(),
    enabled: !!user?.id,
  });

  const markAsReadMutation = useMutation({
    mutationFn: markNoticeAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student-notices"] });
    },
  });

  const hideNoticeMutation = useMutation({
    mutationFn: hideNotice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student-notices"] });
      // Also force refetch to ensure data is updated
      queryClient.refetchQueries({ queryKey: ["student-notices"] });
    },
  });

  const student = studentData?.data;

  // Prevent e-course students from accessing regular student pages
  if (student && student.studentType === "ecourse") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-orange-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Access Restricted
          </h2>
          <p className="text-gray-600 mb-4">
            Notice board is not available for e-course students.
          </p>
          <a
            href="/student/ecourse-dashboard"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to E-Learning Dashboard
          </a>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl border border-red-200/60 rounded-2xl p-6 shadow-lg">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-red-900 font-semibold text-lg">
                Error Loading Notices
              </h3>
              <p className="text-red-700 mt-2">
                Unable to load notices. Please try refreshing the page.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const notices = noticesData?.data || [];
  const pagination = noticesData?.pagination;

  const getNoticeIcon = (type: string) => {
    switch (type) {
      case "urgent":
      case "important":
        return (
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center shadow-md">
            <AlertCircle className="h-5 w-5 text-white" />
          </div>
        );
      case "academic":
        return (
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md">
            <CheckCircle className="h-5 w-5 text-white" />
          </div>
        );
      case "info":
      case "general":
        return (
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-md">
            <Info className="h-5 w-5 text-white" />
          </div>
        );
      default:
        return (
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center shadow-md">
            <Bell className="h-5 w-5 text-white" />
          </div>
        );
    }
  };

  const getNoticeTypeColor = (type: string) => {
    switch (type) {
      case "urgent":
        return "bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-md";
      case "important":
        return "bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md";
      case "academic":
        return "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md";
      case "info":
        return "bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md";
      case "general":
        return "bg-gradient-to-r from-slate-400 to-slate-500 text-white shadow-md";
      default:
        return "bg-gradient-to-r from-slate-400 to-slate-500 text-white shadow-md";
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-md";
      case "medium":
        return "bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-md";
      case "low":
        return "bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md";
      default:
        return "bg-gradient-to-r from-slate-400 to-slate-500 text-white shadow-md";
    }
  };

  const filteredNotices = notices.filter((notice: Notice) => {
    const matchesFilter = filterType === "all" || notice.type === filterType;
    const matchesSearch =
      notice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notice.content.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleViewNotice = (notice: Notice) => {
    setSelectedNotice(notice);
    setShowViewModal(true);
  };

  const handleMarkAsRead = async (noticeId: string) => {
    try {
      await markAsReadMutation.mutateAsync(noticeId);
    } catch (error) {
      console.error("Error marking notice as read:", error);
    }
  };

  const handleHideNotice = async (noticeId: string) => {
    try {
      await hideNoticeMutation.mutateAsync(noticeId);
    } catch (error) {
      console.error("Error hiding notice:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-2 sm:p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-lg sm:text-2xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Notices & Announcements
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">
            Stay updated with important announcements and notices 123
          </p>
        </div>

        {/* Filters and Search */}
        <div className="mb-4 sm:mb-6 bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-4 sm:p-5 hover:shadow-xl transition-all duration-300">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md flex-shrink-0">
                <Filter className="h-5 w-5 text-white" />
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="flex-1 sm:flex-none sm:w-auto border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm hover:shadow-md transition-all duration-300"
              >
                <option value="all">All Types</option>
                <option value="urgent">Urgent</option>
                <option value="important">Important</option>
                <option value="academic">Academic</option>
                <option value="info">Information</option>
                <option value="general">General</option>
              </select>
            </div>
            <div className="flex items-center space-x-2 flex-1">
              <input
                type="text"
                placeholder="Search notices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm hover:shadow-md transition-all duration-300"
              />
            </div>
          </div>
        </div>

        {/* Notices List */}
        <div className="space-y-4 sm:space-y-5">
          {filteredNotices.length > 0 ? (
            filteredNotices.map((notice: Notice) => (
              <div
                key={notice._id}
                className={`bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border-l-4 p-5 sm:p-6 transition-all duration-300 hover:shadow-xl hover:scale-[1.01] ${
                  !notice.isRead ? "border-l-blue-500" : "border-l-slate-300"
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-4">
                  <div className="flex items-start space-x-3 sm:space-x-4 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      {getNoticeIcon(notice.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3
                          className={`text-lg sm:text-xl font-bold break-words ${
                            !notice.isRead ? "text-slate-900" : "text-slate-700"
                          }`}
                        >
                          {notice.title}
                        </h3>
                        {!notice.isRead && (
                          <div className="w-2.5 h-2.5 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex-shrink-0 shadow-md"></div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-slate-600 mb-2">
                        <div className="flex items-center space-x-1.5 bg-gradient-to-br from-slate-50 to-blue-50 px-3 py-1.5 rounded-lg shadow-sm">
                          <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 text-blue-600" />
                          <span className="font-medium">
                            {new Date(notice.publishDate).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1.5 bg-gradient-to-br from-slate-50 to-blue-50 px-3 py-1.5 rounded-lg shadow-sm">
                          <User className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 text-green-600" />
                          <span className="truncate max-w-[150px] sm:max-w-none font-medium">
                            {notice.author.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-row sm:flex-col items-start sm:items-end gap-2 flex-wrap sm:flex-nowrap">
                    <span
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap ${getNoticeTypeColor(
                        notice.type
                      )}`}
                    >
                      {notice.type.charAt(0).toUpperCase() +
                        notice.type.slice(1)}
                    </span>
                    <span
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap ${getPriorityBadge(
                        notice.priority
                      )}`}
                    >
                      {notice.priority.charAt(0).toUpperCase() +
                        notice.priority.slice(1)}{" "}
                      Priority
                    </span>
                  </div>
                </div>

                <div
                  onClick={() => handleViewNotice(notice)}
                  className="cursor-pointer hover:bg-slate-100/50 -mx-2 px-2 py-2 rounded-lg transition-colors mb-4"
                >
                  <p className="text-sm sm:text-base text-slate-700 mb-1 leading-relaxed break-words bg-gradient-to-br from-slate-50 to-blue-50 p-4 rounded-xl line-clamp-3">
                    {notice.content}
                  </p>
                  <span className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1 ml-2">
                    <ExternalLink className="w-3 h-3" />
                    Click to view full content
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-xs sm:text-sm text-slate-600 font-medium bg-white px-3 py-2 rounded-lg shadow-sm w-fit">
                    From: {notice.author.department}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    {!notice.isRead && (
                      <button
                        onClick={() => handleMarkAsRead(notice._id)}
                        disabled={markAsReadMutation.isPending}
                        className="w-full sm:w-auto px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl text-sm font-semibold hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-300"
                      >
                        {markAsReadMutation.isPending
                          ? "Marking..."
                          : "Mark as Read"}
                      </button>
                    )}
                    <button
                      onClick={() => handleHideNotice(notice._id)}
                      disabled={hideNoticeMutation.isPending}
                      className="w-full sm:w-auto px-4 py-2.5 bg-gradient-to-r from-slate-500 to-slate-600 text-white rounded-xl text-sm font-semibold hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:opacity-50 transition-all duration-300"
                    >
                      {hideNoticeMutation.isPending ? "Hiding..." : "Hide"}
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-16 px-4 bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Bell className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">
                No notices found
              </h3>
              <p className="text-sm sm:text-base text-slate-600">
                {searchTerm || filterType !== "all"
                  ? "Try adjusting your search or filter criteria"
                  : "No notices available at the moment"}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6 sm:mt-8 bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-xs sm:text-sm text-slate-700 font-medium text-center sm:text-left bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-2 rounded-lg shadow-sm">
                Showing page {pagination.currentPage} of {pagination.totalPages}{" "}
                ({pagination.totalNotices} total notices)
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={!pagination.hasPrevPage}
                  className="w-full sm:w-auto px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-gradient-to-r hover:from-blue-500 hover:to-indigo-500 hover:text-white hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-slate-700 flex items-center justify-center transition-all duration-300"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </button>
                <div className="flex items-center space-x-2 overflow-x-auto">
                  {Array.from(
                    { length: Math.min(5, pagination.totalPages) },
                    (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-4 py-2.5 text-sm font-semibold rounded-xl whitespace-nowrap transition-all duration-300 ${
                            pageNum === pagination.currentPage
                              ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg scale-110"
                              : "text-slate-700 bg-white border border-slate-300 hover:bg-gradient-to-r hover:from-blue-500 hover:to-indigo-500 hover:text-white hover:shadow-lg"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}
                </div>
                <button
                  onClick={() =>
                    setCurrentPage((prev) =>
                      Math.min(pagination.totalPages, prev + 1)
                    )
                  }
                  disabled={!pagination.hasNextPage}
                  className="w-full sm:w-auto px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-gradient-to-r hover:from-blue-500 hover:to-indigo-500 hover:text-white hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-slate-700 flex items-center justify-center transition-all duration-300"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View Notice Modal */}
      {showViewModal && selectedNotice && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">
                  Notice Details
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedNotice(null);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {selectedNotice.title}
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`px-3 py-1 text-xs rounded-lg ${getNoticeTypeColor(
                      selectedNotice.type
                    )}`}
                  >
                    {selectedNotice.type.charAt(0).toUpperCase() +
                      selectedNotice.type.slice(1)}
                  </span>
                  <span
                    className={`px-3 py-1 text-xs rounded-lg ${getPriorityBadge(
                      selectedNotice.priority
                    )}`}
                  >
                    {selectedNotice.priority.charAt(0).toUpperCase() +
                      selectedNotice.priority.slice(1)}{" "}
                    Priority
                  </span>
                  {!selectedNotice.isRead && (
                    <span className="px-3 py-1 text-xs font-semibold rounded-lg bg-blue-100 text-blue-700">
                      Unread
                    </span>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-6 rounded-xl">
                <h4 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">
                  Content
                </h4>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {selectedNotice.content}
                </p>
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-indigo-600" />
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Published Date
                    </span>
                  </div>
                  <p className="text-gray-900 font-semibold">
                    {new Date(selectedNotice.publishDate).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )}
                  </p>
                </div>

                {selectedNotice.expiryDate && (
                  <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Expiry Date
                      </span>
                    </div>
                    <p className="text-gray-900 font-semibold">
                      {new Date(selectedNotice.expiryDate).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )}
                    </p>
                  </div>
                )}

                {selectedNotice.author && (
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Author
                      </span>
                    </div>
                    <p className="text-gray-900 font-semibold">
                      {selectedNotice.author.name}
                    </p>
                    <p className="text-gray-600 text-sm capitalize">
                      {selectedNotice.author.role}
                    </p>
                  </div>
                )}

                {selectedNotice.author?.department && (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Department
                      </span>
                    </div>
                    <p className="text-gray-900 font-semibold">
                      {selectedNotice.author.department}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              {!selectedNotice.isRead && (
                <button
                  onClick={() => {
                    handleMarkAsRead(selectedNotice._id);
                    setShowViewModal(false);
                    setSelectedNotice(null);
                  }}
                  disabled={markAsReadMutation.isPending}
                  className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {markAsReadMutation.isPending ? "Marking..." : "Mark as Read"}
                </button>
              )}
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedNotice(null);
                }}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:shadow-lg transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentNotices;
